/**
 * Lets a swipe carry the product gallery off a video slide.
 *
 * Broadcast's product-images element advances its fader on swipe only when the
 * active slide is an image — handleMouseUp in theme.js checks the slide's
 * data-type before moving — so a video slide traps the gallery: on mobile there
 * is no way to swipe from the video to the next image, only the thumbs or the
 * arrows get you out.
 *
 * This runs alongside the theme handler and covers exactly the case it skips:
 * video and external_video slides. Image slides stay with the theme, and a 3D
 * model slide is left alone because model-viewer uses drags to rotate.
 */
(() => {
  const GALLERY = "product-images";
  const MEDIA_LIST = "[data-product-media-list]";
  const ACTIVE_SLIDE = "[data-image-id].media--active";
  const SWIPEABLE_MEDIA_TYPES = ["video", "external_video"];

  // Same distance the theme asks for before it treats a drag as a swipe.
  const MINIMUM_SWIPE_DISTANCE = 10;

  // A native video paints its controls along the bottom edge of the element, so
  // a touch that starts in that strip is the scrubber being dragged, not the
  // gallery being swiped.
  const VIDEO_CONTROLS_HEIGHT = 56;

  let swipingGallery = null;
  let startX = 0;
  let startY = 0;

  // Mirrors the theme's own toggleEvents: the fader — and so swipe navigation —
  // is only in play at the viewport the gallery opted into. Outside of it the
  // slides are a natively scrolling row that must keep scrolling.
  const isFaderActive = (gallery) =>
    window.theme?.isMobile
      ? gallery.hasAttribute("data-fader-mobile")
      : gallery.hasAttribute("data-fader-desktop");

  const startsOnVideoControls = (touch) => {
    const video = touch.target?.closest?.("video");
    if (!video) return false;

    const bounds = video.getBoundingClientRect();
    return touch.clientY > bounds.bottom - VIDEO_CONTROLS_HEIGHT;
  };

  const handleTouchStart = (event) => {
    swipingGallery = null;

    const gallery = event.target?.closest?.(MEDIA_LIST)?.closest(GALLERY);
    if (!gallery || !isFaderActive(gallery)) return;
    if (typeof gallery.showNextImage !== "function") return;

    const activeSlide = gallery.querySelector(ACTIVE_SLIDE);
    if (!SWIPEABLE_MEDIA_TYPES.includes(activeSlide?.dataset.type)) return;

    const touch = event.changedTouches[0];
    if (startsOnVideoControls(touch)) return;

    swipingGallery = gallery;
    startX = touch.clientX;
    startY = touch.clientY;
  };

  const handleTouchEnd = (event) => {
    const gallery = swipingGallery;
    swipingGallery = null;

    if (!gallery) return;

    const touch = event.changedTouches[0];
    const distanceX = touch.clientX - startX;
    const distanceY = touch.clientY - startY;

    if (Math.abs(distanceX) <= MINIMUM_SWIPE_DISTANCE) return;
    if (Math.abs(distanceX) <= Math.abs(distanceY)) return;

    distanceX < 0 ? gallery.showNextImage() : gallery.showPreviousImage();
  };

  const handleTouchCancel = () => {
    swipingGallery = null;
  };

  document.addEventListener("touchstart", handleTouchStart, {passive: true});
  document.addEventListener("touchend", handleTouchEnd, {passive: true});
  document.addEventListener("touchcancel", handleTouchCancel, {passive: true});
})();
